import { useState, useEffect } from 'react';
import { Search, X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  path: string;
  type: 'page' | 'banda' | 'evento';
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal = ({ isOpen, onClose }: SearchModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();

  // Updated search data focusing on current navigation items
  const searchableContent: SearchResult[] = [
    {
      id: '1',
      title: 'Início',
      description: 'Página inicial com últimas novidades e destaques',
      path: '/',
      type: 'page'
    },
    {
      id: '2',
      title: 'Bandas',
      description: 'Conheça as bandas parceiras do Sul do Brasil',
      path: '/bandas',
      type: 'page'
    },
    {
      id: '3',
      title: 'Lançamentos',
      description: 'Novos clipes e músicas das bandas parceiras',
      path: '/lancamentos',
      type: 'page'
    },
    {
      id: '4',
      title: 'Agenda de Eventos',
      description: 'Próximos shows e bailes das bandas',
      path: '/agenda-eventos',
      type: 'page'
    },
    {
      id: '6',
      title: 'Cadastre seu evento',
      description: 'Registre seu evento na nossa agenda',
      path: '/cadastre-evento',
      type: 'page'
    },
    {
      id: '7',
      title: 'Sobre nós',
      description: 'História da Essência das Bandas - 8 anos de experiência',
      path: '/sobre',
      type: 'page'
    },
    {
      id: '8',
      title: 'Contato',
      description: 'Entre em contato conosco - Rio Grande do Sul, Brasil',
      path: '/contato',
      type: 'page'
    },
    {
      id: '9',
      title: 'Política de Privacidade',
      description: 'Informações sobre proteção de dados e privacidade',
      path: '/politica-privacidade',
      type: 'page'
    },
    {
      id: '10',
      title: 'Notícias',
      description: 'Últimas notícias do mundo da música',
      path: '/noticias',
      type: 'page'
    }
  ];

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    // Simulate search delay
    const searchTimeout = setTimeout(() => {
      const filteredResults = searchableContent.filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      setSearchResults(filteredResults.slice(0, 8)); // Limit to 8 results
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    onClose();
    setSearchQuery('');
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'banda': return 'text-brand-blue';
      case 'evento': return 'text-accent';
      case 'page': return 'text-brand-purple';
      default: return 'text-muted-foreground';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'banda': return 'Banda';
      case 'evento': return 'Evento';
      case 'page': return 'Página';
      default: return '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-[hsl(var(--painel-card))] border-[hsl(var(--painel-border))]">
        <DialogHeader>
          <DialogTitle className="text-[hsl(var(--painel-foreground))] flex items-center space-x-2">
            <Search size={20} />
            <span>Buscar no site</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Input
              type="text"
              placeholder="Digite para buscar bandas, eventos, páginas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-input border-border focus:border-accent pl-10"
              autoFocus
            />
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          </div>

          {isSearching && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
              <p className="text-muted-foreground mt-2">Buscando...</p>
            </div>
          )}

          {!isSearching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Nenhum resultado encontrado para "{searchQuery}"</p>
            </div>
          )}

          {!isSearching && searchResults.length > 0 && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {searchResults.map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleResultClick(result)}
                  className="w-full text-left p-4 rounded-lg hover:bg-muted/20 transition-colors border border-transparent hover:border-accent/20"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-[hsl(var(--painel-foreground))]">{result.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full bg-muted/20 ${getTypeColor(result.type)}`}>
                          {getTypeLabel(result.type)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{result.description}</p>
                    </div>
                    <ExternalLink size={16} className="text-muted-foreground ml-2 flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}

          {searchQuery.trim().length < 2 && (
            <div className="text-center py-8">
              <Search size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Digite pelo menos 2 caracteres para buscar</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;